<template>
  <div class="tt-page">
    <div class="mb-3">
      <button class="btn btn-sm tt-btn-ghost" @click="$router.push('/trips/settings/vehicles')">
        <i class="bi bi-arrow-left mr-1"></i>Back to Vehicles
      </button>
    </div>

    <TripSettingsNav />

    <div v-if="loading" class="tt-empty">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
    </div>
    <div v-else-if="!vehicle" class="tt-empty">Vehicle not found.</div>

    <template v-else>
      <TripPageHeader
        :title="vehicle.vehicle_name"
        :subtitle="`${vehicle.plate_number} — ${vehicle.type} (${vehicle.gas_type})`"
        icon="bi-car-front"
      />

      <div class="row mb-3">
        <div class="col-sm-6 col-lg-3 mb-3">
          <div class="tt-stat-card tt-stat-card-blue">
            <div class="tt-stat-card-bg"><i class="bi bi-fuel-pump"></i></div>
            <div class="tt-stat-card-content">
              <span class="tt-stat-icon tt-stat-icon-blue"><i class="bi bi-fuel-pump"></i></span>
              <div class="tt-stat-value">{{ nz(vehicle.fuel_balance) }}</div>
              <div class="tt-stat-label">Current Fuel Balance</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3">
          <div class="tt-stat-card tt-stat-card-teal">
            <div class="tt-stat-card-bg"><i class="bi bi-map"></i></div>
            <div class="tt-stat-card-content">
              <span class="tt-stat-icon tt-stat-icon-teal"><i class="bi bi-map"></i></span>
              <div class="tt-stat-value">{{ trips.length }}</div>
              <div class="tt-stat-label">Total Trips</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3">
          <div class="tt-stat-card tt-stat-card-amber">
            <div class="tt-stat-card-bg"><i class="bi bi-speedometer2"></i></div>
            <div class="tt-stat-card-content">
              <span class="tt-stat-icon tt-stat-icon-amber"><i class="bi bi-speedometer2"></i></span>
              <div class="tt-stat-value">{{ totalDistance }}</div>
              <div class="tt-stat-label">Total Km Travelled</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-lg-3 mb-3">
          <div class="tt-stat-card tt-stat-card-rose">
            <div class="tt-stat-card-bg"><i class="bi bi-droplet"></i></div>
            <div class="tt-stat-card-content">
              <span class="tt-stat-icon tt-stat-icon-rose"><i class="bi bi-droplet"></i></span>
              <div class="tt-stat-value">{{ vehicle.distance_per_liter }}</div>
              <div class="tt-stat-label">Km / Liter</div>
            </div>
          </div>
        </div>
      </div>

      <div class="tt-card mb-3">
        <div class="tt-card-body">
          <h6 class="tt-section-title">Fuel Balance Over Time</h6>
          <canvas id="fuel-balance-canvas" height="90"></canvas>
        </div>
      </div>

      <div class="tt-card">
        <div class="tt-card-body">
          <h6 class="tt-section-title">Trip History</h6>
          <div class="table-responsive">
            <table class="table table-sm tt-table mb-0">
              <thead>
                <tr>
                  <th>Date</th><th>Driver</th><th>Destination</th><th>Purpose</th>
                  <th>Status</th><th>Fuel Consumed</th><th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!trips.length">
                  <td colspan="7" class="tt-empty">No trips yet for this vehicle.</td>
                </tr>
                <tr v-for="t in trips" :key="t.id">
                  <td class="text-nowrap">{{ fmtTripDate(t.trip_date) }}</td>
                  <td>{{ t.driver ? t.driver.driver_name : "—" }}</td>
                  <td>{{ t.destination ? t.destination.destination_name : "—" }}</td>
                  <td class="tt-purpose">{{ t.purpose }}</td>
                  <td><span :class="tripStatusClass(t.status)">{{ tripStatusLabel(t.status) }}</span></td>
                  <td>{{ nz(t.deducted) }}</td>
                  <td>{{ nz(t.tank_balance) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import TripPageHeader from "./components/TripPageHeader.vue";
import TripSettingsNav from "./components/TripSettingsNav.vue";
import { tripStatusLabel, tripStatusClass, fmtTripDate } from "./tripHelpers";

export default {
  name: "VehicleDetail",
  components: { TripPageHeader, TripSettingsNav },
  data() {
    return { vehicle: null, trips: [], loading: true, chart: null };
  },
  computed: {
    totalDistance() {
      return this.trips
        .filter((t) => t.sped_balance_travelled)
        .reduce((sum, t) => sum + Number(t.sped_balance_travelled), 0)
        .toFixed(2);
    },
  },
  methods: {
    tripStatusLabel,
    tripStatusClass,
    fmtTripDate,
    nz(v) {
      return v === null || v === undefined ? "—" : v;
    },
    drawChart() {
      const completed = this.trips.filter((t) => t.status === "completed" && t.tank_balance !== null);
      const el = document.getElementById("fuel-balance-canvas");
      if (!el) return;
      if (this.chart) this.chart.destroy();
      this.chart = new Chart(el, {
        type: "line",
        data: {
          labels: completed.map((t) => fmtTripDate(t.trip_date)),
          datasets: [
            {
              label: "Fuel Balance After Trip",
              data: completed.map((t) => Number(t.tank_balance)),
              borderColor: "#4d8dff",
              backgroundColor: "rgba(77,141,255,.15)",
              fill: true,
              tension: 0.3,
            },
          ],
        },
        options: {
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true } },
        },
      });
    },
  },
  mounted() {
    this.$store
      .dispatch("trips/fetchVehicleHistory", this.$route.params.id)
      .then((data) => {
        this.vehicle = data.vehicle;
        this.trips = data.trips;
        this.$nextTick(() => this.drawChart());
      })
      .finally(() => { this.loading = false; });
  },
};
</script>
